import type { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { logoutBackoffice } from '@/auth/logout';
import { BackofficeBrandMark } from './BackofficeBrandMark';
import { BackofficeUserMenu } from './BackofficeUserMenu';
import { GlobalSearchForm } from './GlobalSearchForm';

type BackofficeHeaderProps = {
  isMobileNavigationOpen: boolean;
  onOpenMobileNavigation: () => void;
};

export function BackofficeHeader({
  isMobileNavigationOpen,
  onOpenMobileNavigation,
}: BackofficeHeaderProps) {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const displayName = resolveBackofficeAccountLabel(activeAccount);

  return (
    <header className="bg-white py-4">
      <div className="flex items-center justify-between gap-4 px-5 md:px-8 lg:grid lg:grid-cols-[280px_minmax(0,1fr)_auto] lg:gap-0 lg:px-0">
        <div className="flex min-w-0 items-center gap-3 md:gap-4 lg:px-6">
          <button
            type="button"
            aria-label="Open navigation"
            aria-controls="backoffice-mobile-navigation"
            aria-expanded={isMobileNavigationOpen}
            className="focus-ring inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-neutral-200 text-neutral-700 transition hover:bg-backoffice-50 hover:text-neutral-900 lg:hidden"
            onClick={onOpenMobileNavigation}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <Link
            to="/dashboard"
            aria-label="Nava home"
            className="focus-ring flex shrink-0 flex-col items-end rounded-sm"
          >
            <BackofficeBrandMark />
          </Link>
        </div>

        <div className="hidden min-w-0 lg:block lg:w-[32rem] lg:pr-4">
          <GlobalSearchForm />
        </div>
        {displayName ? (
          <div className="lg:pl-4 lg:pr-8">
            <BackofficeUserMenu
              userLabel={displayName}
              onLogout={() => {
                void logoutBackoffice(instance);
              }}
            />
          </div>
        ) : null}
      </div>
      <div className="mt-3 px-5 md:px-8 lg:hidden">
        <GlobalSearchForm />
      </div>
    </header>
  );
}

function resolveBackofficeAccountLabel(account: AccountInfo | null): string | null {
  if (!account) {
    return null;
  }

  const preferredUsername = account.idTokenClaims?.preferred_username;

  return account.name ?? preferredUsername ?? account.username ?? null;
}
